import React, { useCallback, useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errText, formatDate } from "../../lib/api";
import { card, inputCls, btnPrimary, btnGhost, ROLE_LABEL, timeAgo } from "./common";

const SECTIONS = [["tickets", "Tickets"], ["emails", "E-Mails"], ["tasks", "Aufgaben"], ["customers", "Kunden"]];

function PermRow({ emp, patch }) {
  const perms = emp.panel_perms || {};
  return (
    <tr className="bg-secondary/30">
      <td colSpan={8} className="px-3.5 py-2.5">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="font-bold text-muted-foreground">Bereiche für {emp.name || emp.username}:</span>
          {SECTIONS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 cursor-pointer font-semibold">
              <input type="checkbox" className="accent-[#FF6B35]" checked={perms[k] !== false} data-testid={`perm-${k}-${emp.id}`}
                onChange={(e) => patch(emp.id, { perms: { ...perms, [k]: e.target.checked } }, "Berechtigungen gespeichert")} />
              {l}
            </label>
          ))}
          <span className="text-muted-foreground">Abgewählte Bereiche sind für dieses Konto gesperrt (auch serverseitig).</span>
        </div>
      </td>
    </tr>
  );
}

export default function PanelEmployees({ me }) {
  const [rows, setRows] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [permFor, setPermFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "SUPPORT" });
  const load = useCallback(() => api.get("/admin/employees").then(({ data }) => setRows(data.employees)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/employees", form);
      toast.success(`Mitarbeiter angelegt (Benutzername: ${data.username})`);
      setShowForm(false);
      setForm({ name: "", email: "", password: "", role: "SUPPORT" });
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const patch = async (id, body, ok) => {
    try { await api.patch(`/admin/employees/${id}`, body); toast.success(ok); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="panel-employees">
      <div className="flex items-center">
        <p className="text-sm text-muted-foreground">Mitarbeiter mit Panel-Zugang. Rollen: Support (Tickets & E-Mails), Moderator (+ Moderation & Marktplatz), Admin (alles).</p>
        <button className={`${btnPrimary} ml-auto inline-flex items-center gap-1.5 shrink-0`} onClick={() => setShowForm(!showForm)} data-testid="employee-new-btn"><UserPlus className="w-4 h-4" /> Mitarbeiter anlegen</button>
      </div>
      {showForm && (
        <div className={`${card} p-5 space-y-3`} data-testid="employee-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name-input" />
            <input className={inputCls} placeholder="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email-input" />
            <input className={inputCls} placeholder="Startpasswort (min. 8 Zeichen)" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="employee-password-input" />
            <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="employee-role-select">
              <option value="SUPPORT">Support-Mitarbeiter</option>
              <option value="MODERATOR">Moderator</option>
              {me.role === "SUPER_ADMIN" && <option value="ADMIN">Administrator</option>}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">Der Mitarbeiter meldet sich mit E-Mail + Startpasswort an und sollte das Passwort danach in den Einstellungen ändern.</p>
          <button className={btnPrimary} disabled={!form.name || !form.email || form.password.length < 8} onClick={create} data-testid="employee-create-btn">Konto erstellen</button>
        </div>
      )}
      <div className={`${card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs text-muted-foreground">
              <tr>{["Name", "E-Mail", "Rolle", "Status", "Tickets", "Aufgaben", "Zuletzt aktiv", ""].map((h) => <th key={h} className="text-left font-semibold px-3.5 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody data-testid="employees-table">
              {rows?.map((r) => (
                <React.Fragment key={r.id}>
                <tr className="border-t border-border/60">
                  <td className="px-3.5 py-2.5 font-semibold whitespace-nowrap">{r.name || r.username}{r.id === me.id && <span className="text-xs text-muted-foreground"> (du)</span>}</td>
                  <td className="px-3.5 py-2.5">{r.email}</td>
                  <td className="px-3.5 py-2.5">
                    {r.role === "SUPER_ADMIN" || r.id === me.id ? (ROLE_LABEL[r.role] || r.role) : (
                      <select className="text-xs border border-border rounded-lg px-2 py-1 bg-white" value={r.role} data-testid={`employee-role-${r.id}`}
                        onChange={(e) => patch(r.id, { role: e.target.value }, "Rolle geändert")}>
                        <option value="SUPPORT">Support</option>
                        <option value="MODERATOR">Moderator</option>
                        {me.role === "SUPER_ADMIN" && <option value="ADMIN">Admin</option>}
                      </select>
                    )}
                  </td>
                  <td className="px-3.5 py-2.5"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{r.status === "ACTIVE" ? "Aktiv" : "Deaktiviert"}</span></td>
                  <td className="px-3.5 py-2.5">{r.open_tickets}</td>
                  <td className="px-3.5 py-2.5">{r.open_tasks}</td>
                  <td className="px-3.5 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{r.last_seen_at ? timeAgo(r.last_seen_at) : formatDate(r.created_at)}</td>
                  <td className="px-3.5 py-2.5">
                    <div className="flex items-center gap-3 whitespace-nowrap">
                    {me.role === "SUPER_ADMIN" && r.role !== "SUPER_ADMIN" && (
                      <button className="text-xs font-semibold text-foreground/70 hover:underline" data-testid={`employee-perms-${r.id}`}
                        onClick={() => setPermFor(permFor === r.id ? null : r.id)}>Rechte</button>
                    )}
                    {r.role !== "SUPER_ADMIN" && r.id !== me.id && (
                      <button className="text-xs font-semibold text-red-600 hover:underline" data-testid={`employee-toggle-${r.id}`}
                        onClick={() => window.confirm(r.status === "ACTIVE" ? "Dieses Konto wirklich deaktivieren? Der Mitarbeiter wird sofort abgemeldet." : "Konto wieder aktivieren?") && patch(r.id, { active: r.status !== "ACTIVE" }, r.status === "ACTIVE" ? "Konto deaktiviert" : "Konto aktiviert")}>
                        {r.status === "ACTIVE" ? "Deaktivieren" : "Aktivieren"}
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
                {permFor === r.id && <PermRow emp={r} patch={patch} />}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
